import type { Square, SquareKey } from "./types";

export const FILES = 8;
export const RANKS = 16;

export function squareKey(sq: Square): SquareKey {
  return `${sq.file},${sq.rank}`;
}

export function fromKey(key: SquareKey): Square {
  const [file, rank] = key.split(",").map(Number) as [number, number];
  return { file, rank };
}

export function inBounds(sq: Square): boolean {
  return sq.file >= 0 && sq.file < FILES && sq.rank >= 0 && sq.rank < RANKS;
}

export function sameSquare(a: Square, b: Square): boolean {
  return a.file === b.file && a.rank === b.rank;
}

/** A1..H16 algebraic notation. Files A=0..H=7, ranks "1".."16" map to rank 0..15 with row 1 at rank 0. */
export function toAlgebraic(sq: Square): string {
  return `${"ABCDEFGH"[sq.file]}${sq.rank + 1}`;
}

export function fromAlgebraic(s: string): Square {
  const file = s.charCodeAt(0) - "A".charCodeAt(0);
  const rank = Number(s.slice(1)) - 1;
  return { file, rank };
}

/** Manhattan (orthogonal) distance. */
export function manhattan(a: Square, b: Square): number {
  return Math.abs(a.file - b.file) + Math.abs(a.rank - b.rank);
}

/** Chebyshev (king) distance. */
export function chebyshev(a: Square, b: Square): number {
  return Math.max(Math.abs(a.file - b.file), Math.abs(a.rank - b.rank));
}

/** Returns true if a→b is exactly orthogonal (one axis aligned, other equal) and non-zero. */
export function isOrthogonal(a: Square, b: Square): boolean {
  if (sameSquare(a, b)) return false;
  return a.file === b.file || a.rank === b.rank;
}

/** Returns true if a→b is exactly diagonal (|df| === |dr|) and non-zero. */
export function isDiagonal(a: Square, b: Square): boolean {
  if (sameSquare(a, b)) return false;
  const df = Math.abs(a.file - b.file);
  const dr = Math.abs(a.rank - b.rank);
  return df === dr;
}
